import React, { useEffect, useState } from 'react';
import { istDateTime, istTime } from '../lib/ist';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Wifi, WifiOff, AlertTriangle, ExternalLink, Activity, IndianRupee, MonitorPlay, Bug, Check, MessageSquareWarning, Send, Clock, Save, Camera, CameraOff } from 'lucide-react';
import { API, useAuth, canEdit } from '../App';

const FleetDashboard = () => {
  const { token, user } = useAuth();
  const editable = canEdit(user, 'booths');
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [crashes, setCrashes] = useState([]);
  const [crashLoading, setCrashLoading] = useState(true);
  const [onlyUnresolved, setOnlyUnresolved] = useState(true);
  const [expandedCrash, setExpandedCrash] = useState(null);
  const [timelineBooth, setTimelineBooth] = useState(null);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/fleet/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load fleet');
    }
    setLoading(false);
  };

  const fetchCrashes = async () => {
    try {
      const res = await axios.get(
        `${API}/fleet/crash-reports?limit=50${onlyUnresolved ? '&only_unresolved=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCrashes(res.data.crashes || []);
    } catch (e) {
      // Non-fatal — the fleet page still renders
    }
    setCrashLoading(false);
  };

  const resolveCrash = async (crash) => {
    try {
      await axios.post(
        `${API}/kiosks/${crash.kiosk_id}/crash-reports/${crash.id}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCrashes();
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    fetchOverview();
    fetchCrashes();
    const t = setInterval(() => {
      fetchOverview();
      fetchCrashes();
    }, 15000);  // refresh every 15s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyUnresolved]);

  if (loading) return <div className="text-charcoal-800/40 text-center py-12">Loading fleet…</div>;
  if (error) return <div className="text-red-600 text-center py-12">{error}</div>;
  if (!data) return null;

  const s = data.summary;
  const bandColor = (band) => band === 'healthy' ? 'bg-green-500' : band === 'warning' ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="space-y-6" data-testid="fleet-dashboard">
      {timelineBooth && (
        <CameraTimelineModal booth={timelineBooth} token={token} onClose={() => setTimelineBooth(null)} />
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-charcoal-900">Fleet</h1>
          <p className="text-sm text-charcoal-800/60 mt-1">
            {s.total} booth{s.total === 1 ? '' : 's'} · {s.online}/{s.total} online ·
            <span className="text-green-700 ml-1">{s.healthy} healthy</span>,
            <span className="text-amber-700 ml-1">{s.warning} warning</span>,
            <span className="text-red-700 ml-1">{s.down} down</span>
          </p>
        </div>
        <button onClick={fetchOverview} className="flex items-center gap-1.5 text-sm border border-charcoal-900/10 px-3 py-1.5 rounded-lg hover:bg-cream-50">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<IndianRupee className="w-5 h-5 text-green-600" />} label="Revenue today" value={`₹${s.revenue_today}`} />
        <StatCard icon={<MonitorPlay className="w-5 h-5 text-blue-600" />} label="Live booths" value={`${s.online} / ${s.total}`} />
        <StatCard icon={<Activity className="w-5 h-5 text-purple-600" />} label="Avg health" value={`${s.avg_health}%`} />
        <StatCard icon={<AlertTriangle className="w-5 h-5 text-amber-600" />} label="Need attention" value={s.down + s.warning} />
      </div>

      {/* Booth grid */}
      <div className="card-dashboard p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="fleet-table">
          <thead className="bg-cream-50">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Booth</th>
              <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Health</th>
              <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Status</th>
              <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Camera</th>
              <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Today</th>
              <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Paper</th>
              <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase" title="Print lag: dispatch → paper-out, measured by the printer's media counter (7 days, needs kiosk v3.17.0+). Help: WhatsApp help-QR scans in 7 days.">Lag · Help 7d</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-charcoal-900/5">
            {data.booths.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-charcoal-800/40">No booths yet</td></tr>
            ) : data.booths.map(b => (
              <tr key={b.id} className="hover:bg-cream-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-charcoal-900">{b.name}</div>
                  <div className="text-xs text-charcoal-800/50">{b.venue_name || b.location || '—'}{b.city ? ` · ${b.city}` : ''}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-cream-100 rounded-full overflow-hidden">
                      <div className={`h-full ${bandColor(b.health_band)}`} style={{ width: `${b.health_score}%` }}></div>
                    </div>
                    <span className="font-mono text-xs text-charcoal-800/70 w-8">{b.health_score}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {Object.entries(b.health_breakdown).map(([k, v]) => (
                      <span key={k} className="text-[9px] uppercase text-charcoal-800/40" title={`${k}: ${v}`}>
                        {k.charAt(0)}{v}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {b.online ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700"><Wifi className="w-3 h-3" /> Online</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-red-700"><WifiOff className="w-3 h-3" /> Offline</span>
                  )}
                  {b.last_seen && (
                    <div className="text-[10px] text-charcoal-800/40 mt-0.5">last: {istTime(b.last_seen)}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`fleet-camera-${b.id}`}>
                  <button
                    onClick={() => setTimelineBooth(b)}
                    className="text-left hover:opacity-70 transition-opacity"
                    title="View camera & power event timeline"
                    data-testid={`fleet-camera-timeline-btn-${b.id}`}
                  >
                    {!b.online ? (
                      <span className="text-charcoal-800/30">—</span>
                    ) : b.camera_status === 'failed' ? (
                      <span className="inline-flex items-center gap-1 text-red-700 font-medium"><CameraOff className="w-3 h-3" /> Not found</span>
                    ) : b.camera_status === 'ok' ? (
                      <span className="inline-flex items-center gap-1 text-green-700"><Camera className="w-3 h-3" /> OK{b.camera_mode === 'native' ? ' · ffmpeg' : ''}</span>
                    ) : (
                      <span className="text-charcoal-800/40" title="Booth hasn't started a camera session yet">no data</span>
                    )}
                    {b.camera_status_at && b.camera_status && b.online && (
                      <div className="text-[10px] text-charcoal-800/40 mt-0.5">{istTime(b.camera_status_at)}</div>
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-medium">₹{b.revenue_today}</div>
                  <div className="text-charcoal-800/50">{b.orders_today} orders</div>
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`fleet-paper-${b.id}`}>
                  {b.media_remaining == null ? (
                    <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title="Install DNP PrinterInfo on the booth PC + kiosk v3.14.3+">setup needed</span>
                  ) : (
                    <>
                      <div className={`font-mono font-medium ${
                        b.media_remaining <= 50 ? 'text-red-600' : b.media_remaining <= 100 ? 'text-amber-600' : 'text-green-700'
                      }`}>{b.media_remaining}</div>
                      <div className="text-[9px] uppercase text-charcoal-800/40">printer</div>
                    </>
                  )}
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`fleet-support-${b.id}`}>
                  {b.print_latency ? (
                    <div
                      className={`font-mono font-medium ${
                        b.print_latency.slow_count > 0 ? 'text-red-600' : 'text-green-700'
                      }`}
                      title={`Dispatch → paper-out (7d): avg ${Math.round(b.print_latency.avg_ms / 1000)}s, max ${Math.round(b.print_latency.max_ms / 1000)}s across ${b.print_latency.measured} prints. ${b.print_latency.slow_count} slower than 2 min.`}
                    >
                      {Math.round(b.print_latency.avg_ms / 1000)}s
                      {b.print_latency.slow_count > 0 && <span className="ml-1 text-[9px] uppercase bg-red-100 text-red-700 px-1 py-0.5 rounded">{b.print_latency.slow_count} slow</span>}
                    </div>
                  ) : (
                    <span className="text-charcoal-800/30" title="No paper-out measurements yet — needs kiosk v3.17.0+">—</span>
                  )}
                  <div className={`text-[10px] mt-0.5 ${b.help_scans_7d > 0 ? 'text-amber-700 font-medium' : 'text-charcoal-800/40'}`} title="WhatsApp help-QR scans by customers in the last 7 days">
                    {b.help_scans_7d || 0} help scan{b.help_scans_7d === 1 ? '' : 's'}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => nav(`/admin/kiosks/${b.id}`)}
                    data-testid={`fleet-open-${b.id}`}
                    className="text-xs px-3 py-1.5 border border-charcoal-900/10 rounded-lg hover:bg-cream-100 inline-flex items-center gap-1"
                  >
                    Console <ExternalLink className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-charcoal-800/40 text-center">Auto-refreshes every 15 seconds</p>

      {/* Crash Telemetry panel — silent React render errors reported by kiosks */}
      <div className="card-dashboard p-0 overflow-hidden" data-testid="fleet-crash-panel">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-charcoal-900/5 flex-wrap">
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-red-600" />
            <h2 className="font-serif text-lg text-charcoal-900">Crash telemetry</h2>
            <span className="text-xs text-charcoal-800/50">
              {crashLoading ? 'Loading…' : `${crashes.length} crash${crashes.length === 1 ? '' : 'es'}`}
            </span>
          </div>
          <label className="flex items-center gap-2 text-xs text-charcoal-800/70">
            <input
              type="checkbox"
              checked={onlyUnresolved}
              onChange={(e) => setOnlyUnresolved(e.target.checked)}
              data-testid="fleet-crash-only-unresolved"
            />
            Only show unresolved
          </label>
        </div>
        {!crashLoading && crashes.length === 0 ? (
          <div className="text-center py-8 text-charcoal-800/40 text-sm">
            No {onlyUnresolved ? 'unresolved ' : ''}crashes reported. Booths are quiet.
          </div>
        ) : (
          <ul className="divide-y divide-charcoal-900/5">
            {crashes.map(c => (
              <li key={c.id} className="px-4 py-3 hover:bg-cream-50" data-testid={`fleet-crash-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-charcoal-900 text-sm">{c.kiosk_name || 'Unknown booth'}</span>
                      {c.screen && (
                        <span className="text-[10px] uppercase tracking-wider bg-cream-100 px-1.5 py-0.5 rounded text-charcoal-800/70">
                          {c.screen}
                        </span>
                      )}
                      {c.kiosk_version && (
                        <span className="text-[10px] text-charcoal-800/40 font-mono">v{c.kiosk_version}</span>
                      )}
                      {c.resolved && (
                        <span className="text-[10px] uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          resolved
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-red-700 mt-1 font-mono truncate">{c.message}</div>
                    <div className="text-[10px] text-charcoal-800/40 mt-0.5">
                      {istDateTime(c.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setExpandedCrash(expandedCrash === c.id ? null : c.id)}
                      className="text-xs px-2 py-1 border border-charcoal-900/10 rounded hover:bg-cream-100"
                      data-testid={`fleet-crash-expand-${c.id}`}
                    >
                      {expandedCrash === c.id ? 'Hide' : 'Stack'}
                    </button>
                    {!c.resolved && editable && (
                      <button
                        onClick={() => resolveCrash(c)}
                        className="text-xs px-2 py-1 border border-green-600/30 text-green-700 rounded hover:bg-green-50 inline-flex items-center gap-1"
                        data-testid={`fleet-crash-resolve-${c.id}`}
                      >
                        <Check className="w-3 h-3" /> Resolve
                      </button>
                    )}
                    <button
                      onClick={() => nav(`/admin/kiosks/${c.kiosk_id}`)}
                      className="text-xs px-2 py-1 border border-charcoal-900/10 rounded hover:bg-cream-100 inline-flex items-center gap-1"
                      data-testid={`fleet-crash-open-${c.id}`}
                    >
                      Booth <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                {expandedCrash === c.id && (
                  <pre className="mt-3 bg-charcoal-900 text-red-200 text-[10px] p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap break-all">
{c.stack || '(no stack)'}
{c.component_stack ? `\n\n--- component stack ---\n${c.component_stack}` : ''}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* SMS Alerts panel */}
      <SmsAlertsPanel token={token} editable={editable} />
      {/* Alert quiet-hours settings */}
      {editable && <AlertSettingsCard token={token} />}
    </div>
  );
};

const ALERT_TYPE_LABELS = {
  booth_crash: { label: 'Crash', cls: 'bg-red-100 text-red-700' },
  camera_failover: { label: 'Camera failover', cls: 'bg-amber-100 text-amber-700' },
  print_failed: { label: 'Print failed', cls: 'bg-orange-100 text-orange-700' },
  booth_offline: { label: 'Offline', cls: 'bg-red-100 text-red-700' },
  booth_online: { label: 'Back online', cls: 'bg-green-100 text-green-700' },
  low_media: { label: 'Low paper', cls: 'bg-amber-100 text-amber-700' },
  test_sms: { label: 'Test', cls: 'bg-blue-100 text-blue-700' },
};

const SmsAlertsPanel = ({ token, editable }) => {
  const [status, setStatus] = useState(null);
  const [testMsg, setTestMsg] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/alerts/sms-status?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendTest = async (channel) => {
    setSending(true);
    setTestMsg(null);
    try {
      const res = await axios.post(`${API}/admin/alerts/test-${channel}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestMsg({ ok: channel === 'email' ? !!res.data.ok : res.data.status === 'sent', text: channel === 'email' ? 'Test email sent ✓' : `Test SMS status: ${res.data.status}` });
      fetchStatus();
    } catch (e) {
      setTestMsg({ ok: false, text: e.response?.data?.detail || `Test ${channel} failed` });
    }
    setSending(false);
  };

  if (!status) return null;
  const cfg = status.config || {};
  const emailCfg = status.email_config || {};
  const alerts = status.recent_alerts || [];

  return (
    <div className="card-dashboard p-0 overflow-hidden" data-testid="fleet-sms-panel">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-charcoal-900/5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <MessageSquareWarning className="w-4 h-4 text-blue-600" />
          <h2 className="font-serif text-lg text-charcoal-900">Booth alerts</h2>
          {emailCfg.enabled ? (
            <span className="text-[10px] uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5 rounded" data-testid="email-status-enabled">email active</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid="email-status-disabled">email keys not set</span>
          )}
          {cfg.enabled ? (
            <span className="text-[10px] uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5 rounded" data-testid="sms-status-enabled">sms active</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider bg-cream-100 text-charcoal-800/50 px-1.5 py-0.5 rounded" data-testid="sms-status-disabled">sms off</span>
          )}
          <span className="text-xs text-charcoal-800/50">→ {(emailCfg.alert_emails || []).join(', ') || (cfg.alert_numbers || []).join(', ')}</span>
        </div>
        <div className="flex items-center gap-2">
          {testMsg && (
            <span className={`text-xs ${testMsg.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="sms-test-result">{testMsg.text}</span>
          )}
          {editable && (
          <button
            onClick={() => sendTest('email')}
            disabled={sending}
            data-testid="email-send-test-btn"
            className="text-xs px-3 py-1.5 border border-charcoal-900/10 rounded-lg hover:bg-cream-100 inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Send className="w-3 h-3" /> {sending ? 'Sending…' : 'Send test email'}
          </button>
          )}
        </div>
      </div>
      {!emailCfg.enabled && (
        <div className="px-4 py-2 bg-amber-50 text-amber-800 text-xs border-b border-amber-100">
          Alerts are being recorded but not delivered. Add <span className="font-mono">GMAIL_USERNAME</span> and <span className="font-mono">GMAIL_APP_PASSWORD</span> to the backend to start emailing {(emailCfg.alert_emails || []).join(' & ')}.
        </div>
      )}
      {alerts.length === 0 ? (
        <div className="text-center py-8 text-charcoal-800/40 text-sm">No alerts fired yet. That's a good thing.</div>
      ) : (
        <ul className="divide-y divide-charcoal-900/5 max-h-80 overflow-auto">
          {alerts.map(a => {
            const t = ALERT_TYPE_LABELS[a.alert_type] || { label: a.alert_type, cls: 'bg-cream-100 text-charcoal-800/70' };
            return (
              <li key={a.id} className="px-4 py-2.5 hover:bg-cream-50 flex items-start justify-between gap-3" data-testid={`sms-alert-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${t.cls}`}>{t.label}</span>
                    <span className="font-medium text-charcoal-900 text-sm">{a.kiosk_name}</span>
                    <span className="text-[10px] text-charcoal-800/40">{istDateTime(a.created_at)}</span>
                  </div>
                  <div className="text-xs text-charcoal-800/60 mt-0.5 truncate">{a.message}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider shrink-0 px-1.5 py-0.5 rounded ${
                  a.status === 'sent' ? 'bg-green-100 text-green-700'
                    : a.status === 'disabled' ? 'bg-cream-100 text-charcoal-800/50'
                      : a.status === 'skipped_cooldown' ? 'bg-cream-100 text-charcoal-800/50'
                        : 'bg-red-100 text-red-700'
                }`}>
                  {a.status === 'skipped_cooldown' ? 'cooldown' : a.status === 'disabled' ? 'not sent' : a.status}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const AlertSettingsCard = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/alert-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSettings(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await axios.put(`${API}/admin/alert-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(res.data);
      setMsg({ ok: true, text: 'Saved ✓' });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || 'Save failed (super admin only)' });
    }
    setSaving(false);
  };

  if (!settings) return null;
  return (
    <div className="card-dashboard" data-testid="alert-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4 text-purple-600" />
        <h2 className="font-serif text-lg text-charcoal-900">Alert quiet hours</h2>
      </div>
      <p className="text-xs text-charcoal-800/60 mb-4">
        No ON/OFF booth emails during quiet hours (IST). Outside the window you get one first-ON and one OFF email per day.
        The daily check emails you if a booth still isn't on by the time below.
      </p>
      <div className="flex items-end gap-4 flex-wrap">
        {[
          ['quiet_start', 'Quiet from'],
          ['quiet_end', 'Quiet until'],
          ['open_check_time', '"Still not on" check at'],
        ].map(([key, label]) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-charcoal-800/50">{label}</span>
            <input
              type="time"
              value={settings[key] || ''}
              onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
              className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm focus:outline-none focus:border-charcoal-900/30"
              data-testid={`alert-setting-${key}`}
            />
          </label>
        ))}
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-1.5 text-sm bg-charcoal-900 text-white px-4 py-2 rounded-lg hover:bg-charcoal-800 disabled:opacity-50"
          data-testid="alert-settings-save-btn"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && (
          <span className={`text-xs ${msg.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="alert-settings-save-result">{msg.text}</span>
        )}
      </div>
    </div>
  );
};

const TIMELINE_STYLE = {
  setup_failed: { dot: 'bg-red-500', text: 'text-red-700' },
  native_fallback_failed: { dot: 'bg-red-500', text: 'text-red-700' },
  device_snapshot: { dot: 'bg-red-400', text: 'text-red-600' },
  device_change: { dot: 'bg-sky-500', text: 'text-sky-700' },
  stream_started: { dot: 'bg-green-500', text: 'text-green-700' },
  native_stream_started: { dot: 'bg-green-500', text: 'text-green-700' },
  auto_recovered: { dot: 'bg-green-600', text: 'text-green-700' },
  native_mode_enabled: { dot: 'bg-amber-500', text: 'text-amber-700' },
  usb_replug_attempt: { dot: 'bg-violet-500', text: 'text-violet-700' },
  usb_replug_result: { dot: 'bg-violet-500', text: 'text-violet-700' },
  power: { dot: 'bg-amber-400', text: 'text-amber-700' },
  'camera-usb': { dot: 'bg-violet-400', text: 'text-violet-700' },
};

const CameraTimelineModal = ({ booth, token, onClose }) => {
  const [days, setDays] = useState(7);
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setEvents(null);
    axios.get(`${API}/kiosks/${booth.id}/camera-timeline?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setEvents(r.data.events)).catch((e) => setErr(e.response?.data?.detail || 'Failed to load timeline'));
  }, [booth.id, days, token]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="camera-timeline-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-cream-200">
          <div>
            <h3 className="font-serif text-lg text-charcoal-900">Camera timeline — {booth.name}</h3>
            <p className="text-xs text-charcoal-800/50">Camera drops, recoveries, USB replugs & system power events (IST)</p>
          </div>
          <div className="flex items-center gap-2">
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`text-xs px-2.5 py-1 rounded-full ${days === d ? 'bg-charcoal-900 text-white' : 'bg-cream-100 text-charcoal-800/60 hover:bg-cream-200'}`}
                data-testid={`timeline-days-${d}`}
              >
                {d}d
              </button>
            ))}
            <button onClick={onClose} className="ml-2 text-charcoal-800/50 hover:text-charcoal-900 text-xl leading-none" data-testid="timeline-close-btn">×</button>
          </div>
        </div>
        <div className="overflow-y-auto px-5 py-4 flex-1" data-testid="camera-timeline-list">
          {err && <p className="text-sm text-red-600 text-center py-8">{err}</p>}
          {!err && events === null && <p className="text-sm text-charcoal-800/40 text-center py-8">Loading…</p>}
          {events !== null && events.length === 0 && (
            <p className="text-sm text-charcoal-800/40 text-center py-8">
              No camera or power events in the last {days} day{days > 1 ? 's' : ''}.<br />
              <span className="text-xs">Plug/unplug, power and replug tracking requires booth v3.16.0+.</span>
            </p>
          )}
          {events !== null && events.length > 0 && (
            <ol className="relative border-l border-cream-200 ml-2 space-y-4">
              {events.map((e, i) => {
                const st = TIMELINE_STYLE[e.event] || (e.level === 'error' ? TIMELINE_STYLE.setup_failed : { dot: 'bg-charcoal-300', text: 'text-charcoal-800' });
                return (
                  <li key={i} className="ml-4">
                    <span className={`absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full ${st.dot}`} />
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[11px] text-charcoal-800/40 tabular-nums whitespace-nowrap">{istDateTime(e.ts)}</span>
                      <span className={`text-xs font-medium ${st.text}`}>{e.event}</span>
                      <span className="text-[10px] uppercase tracking-wider text-charcoal-800/30">{e.source}</span>
                    </div>
                    <p className="text-xs text-charcoal-800/70 mt-0.5 break-words">{e.message}</p>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="card-dashboard">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg bg-cream-100">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-charcoal-800/50">{label}</div>
        <div className="font-serif text-xl text-charcoal-900">{value}</div>
      </div>
    </div>
  </div>
);

export default FleetDashboard;
